/**
 * ココシル検索の検索結果画面
 *
 * * App
 *     * [KokosilResult]{@link module:KokosilResult~KokosilResult}
 *     * [KokosilMap]{@link module:KokosilMap~KokosilMap}
 *         * [SearchNextButton]{@link module:KokosilResult~SearchNextButton}
 *
 * @module KokosilResult
 */
import React from 'react';
import { Row, Col, Alert, Button, Card, Spinner } from 'react-bootstrap';
import { BsArrowClockwise } from 'react-icons/bs';

import * as KokosilConfig from './KokosilConfig';
import KokosilMap from './KokosilMap';

/**
 * 「検索中...」表示の React Component
 *
 * ```js
 * return (
 *   <SearchNextButton nextSearch={nextSearchCallback} />
 * );
 * ```
 * nextSearch は [App#nextSearch]{@link module:App~App#nextSearch} への callback。
 */
class SearchNextButton extends React.Component {
  constructor(props) {
    super(props);
    this.myRef = React.createRef();
  }

  componentDidMount() {
    // IntersectionOverver を使って画面に現れたことを検出したら検索処理
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // サーバに負荷をかけないように検索開始まで 250ms 待つ
          setTimeout( () => this.props.nextSearch(), 250);
        }
      },
      { threshold: 0.5 }
    );
    this.observer.observe(this.myRef.current);
  }

  componentWillUnmount() {
    this.observer.unobserve(this.myRef.current);
  }

  render() {
    return <Button ref={this.myRef} variant="light" className="mb-2"><Spinner animation="border" size="sm" role="status" /> 検索中...</Button>;
  }
}

/**
 * 検索結果画面の Resct Component
 *
 * ```js
 * return (
 *   <KokosilResult
 *     nextSearch={nextSearchCallback}
 *     searchState={state}
 *     setMode={setModeFn}
 *   />
 * );
 * ```
 *
 * nextSearch は [App#nextSearch]{@link module:App~App#nextSearch} への
 * callback。 [SearchNextButton]{@link module:KokosilResult~SearchNextButton}に渡
 * される。
 *
 * searchState には [App#state]{@link module:App~App#state} を渡す。
 */
class KokosilResult extends React.Component {
  componentDidUpdate() {
    const state = this.props.searchState;
    window.sessionStorage.setItem("state", JSON.stringify(state));
  }

  // 地図ボタンクリック。地図に切り替え、残りの全部の検索を開始。
  onClickMapButton = e => {
    document.location.hash="m=map";
    window.history.pushState({ mode:"map", keyword: this.props.searchState.keyword, categories: this.props.searchState.categories }, '', '');
    this.props.setMode("map");

    setTimeout( () => {
      const vh = window.innerHeight;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    }, 1000)

    const searchNext = () => {
      setTimeout( () => {
        if (this.props.searchState.hasNext && this.props.searchState.mode === "map") {
          if (!this.props.searchState.searching) {
            this.props.nextSearch();
          }
          searchNext();
        }
      }, 250 );
    }
    searchNext();
  }

  // 「検索条件」Button
  onClickFormButton = () => {
    document.location.hash = "m=form";
    window.history.pushState({ mode:"form", keyword: this.props.searchState.keyword, categories: this.props.searchState.categories }, '', '');
    this.props.setMode("form");
  }

  // 「一覧表示」Button
  onClickResultButton = () => {
    document.location.hash = "m=result";
    window.history.pushState({ mode:"result", keyword: this.props.searchState.keyword, categories: this.props.searchState.categories }, '', '');
    this.props.setMode("result");
  }

  // render
  render() {
    let result;
    result = this.props.searchState.spots.map((spot, idx) => {
      return (
        <Col key={idx} xs={6} sm={4} md={3} lg={2} className="resultCol">
        <a href={KokosilConfig.KOKOSIL_TOP + "/ja/place/" + spot.ucode }>
        <Card>
        <Card.Img variant="top" src={spot.thumbnail || "./images/no-image.jpg"} />
        <Card.Body>
        <Card.Title>{spot.name}</Card.Title>
        <Card.Text>{spot.description}</Card.Text>
        </Card.Body>
        </Card>
        </a>
        </Col>
      );
    });
    if (result.length === 0 && !this.props.searchState.searching && !this.props.searchState.searchError) {
      result = <Col>検索結果は 0 件です。</Col>
    }

    let message;
    if (this.props.searchState.searchError) {
      message = (
        <Col>
        <Alert variant="danger">通信エラーが発生しました。{" "}
        <Button variant="danger" onClick={this.props.nextSearch} ><BsArrowClockwise /></Button>
        </Alert>
        </Col>
      );
    }
    else if (this.props.searchState.searching) {
      message = (
        <Col><Button variant="light" className="mb-2"><Spinner animation="border" size="sm" role="status" /> 検索中...</Button></Col>
      );
    }
    else if (this.props.searchState.hasNext) {
      message = (
        <Col><SearchNextButton nextSearch={this.props.nextSearch} /></Col>
      );
    }
    else {
      message = null;
    }

    let mapButtons;
    if (this.props.searchState.mode === "result") {
      mapButtons = (
        <>
        <Button id="result-back-btn" variant="primary" onClick={this.onClickFormButton}>検索条件</Button>
        <Button id="map-btn" variant="success" onClick={this.onClickMapButton}>地図で表示する</Button>
        <Button id="count-btn" variant="light">{this.props.searchState.spots.length}/{this.props.searchState.count_all}</Button>
        </>
      );
    }
    else { // 地図画面
      if (this.props.searchState.hasNext) {
        mapButtons = (
          <>
          <Button id="result-back-btn" variant="primary" onClick={this.onClickFormButton}>検索に戻る</Button>
          <Button id="map-back-btn" variant="success" onClick={this.onClickResultButton}>一覧で表示する</Button>
          <Button id="count-btn" variant="light"><Spinner animation="border" size="sm" role="status" />{this.props.searchState.spots.length}/{this.props.searchState.count_all}</Button>
          </>
        );
        const searchNext = () => {
          setTimeout( () => {
            if (this.props.searchState.hasNext && this.props.searchState.mode === "map") {
              if (!this.props.searchState.searching) {
                this.props.nextSearch();
              }
              searchNext();
            }
          }, 250 );
        }
        searchNext();
      }
      else {
        mapButtons = (
          <>
          <Button id="result-back-btn" variant="primary" onClick={this.onClickFormButton}>検索条件</Button>
          <Button id="map-back-btn" variant="success" onClick={this.onClickResultButton}>一覧で表示する</Button>
          <Button id="count-btn" variant="light">{this.props.searchState.spots.length}/{this.props.searchState.count_all}</Button>
          </>
        );
      }
    }

    const resultListHidden = this.props.searchState.mode !== "result" ? " hidden" : "";
    const resultMapHidden  = this.props.searchState.mode !== "map"    ? " hidden" : "";
    return (
      <>
      <div className="map-btn-div">
        <Col id="map-btn-col">{mapButtons}</Col>
      </div>
      <div className={"resultList" + resultListHidden}>
        <Row className="result-div">{result}</Row>
        <Row className="mt-2 mb-2">{message}</Row>
      </div>
      {!resultMapHidden && (
      <div className={"resultMap" + resultMapHidden}>
        <KokosilMap searchState={this.props.searchState} />
      </div>
      )}
      </>
    );
  }
}

export default KokosilResult;
